import { useTheme } from "@mui/material/styles";
import React from "react";
import * as ReactDom from "react-dom";
import * as JsxRuntime from "react/jsx-runtime";
import type { CoreContentTemplate, CoreContentTemplateSearchRequest, CoreContentTemplateSearchResponse } from "../../api/CoreContentTemplatesClient";
import type { ReactChatClient } from "../../api/ReactChatClient";
import type { ReactCoreClient } from "../../api/ReactCoreClient";
import type { IToastService } from "../../common/hooks/useToastService";
import useDebouncedValue from "../../common/hooks/useDebouncedValue";
import useDebouncedCallback from "../../common/hooks/useDebouncedCallback";
import ChatActivity from "../Components/Chat/ChatActivity";
import { SdkStyledMarkdown } from "../Components/Chat/StyledMarkdown";
import { useChatSession } from "../Hooks/useChatSession";
import { ReactEnumContext } from "./ReactEnumContext";
import FilePickerDialog from "../Components/Files/FilePickerDialog";
import { loadReactRuntimeIconModules, reactRuntimeModules, REACT_LIBRARY_VERSIONS } from "./ReactRuntimeLibraryModules";

export type { CoreResourceSearchRequest as CoreFileSearchRequest } from "../../api/ReactCoreClient";
export { REACT_LIBRARY_VERSIONS };

const MuiBox = reactRuntimeModules["@mui/material/Box"].default;
const MuiButton = reactRuntimeModules["@mui/material/Button"].default;
const MuiStack = reactRuntimeModules["@mui/material/Stack"].default;
const MuiTextField = reactRuntimeModules["@mui/material/TextField"].default;
const MuiTypography = reactRuntimeModules["@mui/material/Typography"].default;
const NativeChatMessages = React.lazy(() => import("../Components/Chat/SdkChatComponents").then((module) => ({ default: module.SdkChatMessages })));
const NativeChatComposer = React.lazy(() => import("../Components/Chat/SdkChatComponents").then((module) => ({ default: module.SdkChatComposer })));

export const REACT_RUNTIME_API_VERSION = "1.0";
export const REACT_COMPONENT_SDK_VERSION = "1.10";
export const SUPPORTED_REACT_COMPONENT_SDK_VERSIONS = ["1.0", "1.1", "1.2", "1.3", "1.4", "1.5", "1.6", "1.7", "1.8", "1.9", REACT_COMPONENT_SDK_VERSION] as const;
export const REACT_RUNTIME_BRIDGE_SYMBOL_KEY = "randol.react.runtime.v1";
export const REACT_VERSION_RANGE = ">=18.3.0 <19.0.0";
export const REACT_DOM_VERSION_RANGE = ">=18.3.0 <19.0.0";

export type ReactCurrentUser = Readonly<{ Id: string; Name: string }>;

export type ReactComponentContext = {
  /** Authenticated runtime identity, absent for anonymous/identity-less embed sessions. Not authorization evidence. */
  readonly currentUser: ReactCurrentUser | undefined;
  enums: ReactEnumContext;
  route: Readonly<{ params: Readonly<Record<string, string | undefined>> }>;
  toastService: IToastService;
  core: ReturnType<ReactCoreClient["forHost"]> & {
    chat: ReturnType<ReactChatClient["forHost"]>;
    contentTemplates: {
      search: (request?: CoreContentTemplateSearchRequest) => Promise<CoreContentTemplateSearchResponse>;
      getById: (templateId: string) => Promise<CoreContentTemplate>;
    };
  };
  invoke: <TRequest = unknown, TResponse = unknown>(operationReference: string, request?: TRequest) => Promise<TResponse>;
  emit: (name: string, value?: unknown) => Promise<unknown>;
  navigate: (path: string) => void;
};

export type ReactComponentRuntimeProps<TInput = unknown> = {
  input?: TInput;
  ctx: ReactComponentContext;
};

type ReactRuntimeBridge = {
  RuntimeApiVersion: string;
  ReactVersion: string;
  ReactDomVersion: string;
  SdkVersion: string;
  React: typeof React;
  ReactDom: Pick<typeof ReactDom, "createPortal">;
  JsxRuntime: typeof JsxRuntime;
  LibraryVersions: Readonly<Record<string, string>>;
  Modules: Readonly<Record<string, Readonly<Record<string, unknown>>>>;
  Sdk: {
    Box: typeof MuiBox;
    Button: typeof MuiButton;
    Stack: typeof MuiStack;
    TextField: typeof MuiTextField;
    Typography: typeof MuiTypography;
    useTheme: typeof useTheme;
    useDebouncedValue: typeof useDebouncedValue;
    useDebouncedCallback: typeof useDebouncedCallback;
    useChatSession: typeof useChatSession;
    StyledMarkdown: typeof SdkStyledMarkdown;
    ChatMessages: typeof NativeChatMessages;
    ChatComposer: typeof NativeChatComposer;
    ChatActivity: typeof ChatActivity;
    FilePickerDialog: typeof FilePickerDialog;
  };
};

const reactRuntimeBridgeSymbol = Symbol.for(REACT_RUNTIME_BRIDGE_SYMBOL_KEY);
const runtimeScope = globalThis as unknown as Record<symbol, ReactRuntimeBridge | undefined>;

const runtimeModules: Record<string, Readonly<Record<string, unknown>>> = { ...reactRuntimeModules };

export async function ensureReactRuntimeIconModules(importPaths: readonly string[]): Promise<void> {
  if (importPaths.length === 0) return;

  const bridge = runtimeScope[reactRuntimeBridgeSymbol];
  if (!bridge || Object.isFrozen(bridge.Modules)) {
    throw new Error("The React runtime bridge cannot load Material icons. Refresh app-runtime and try again.");
  }

  const modules = bridge.Modules as Record<string, Readonly<Record<string, unknown>>>;
  const missingPaths = importPaths.filter((importPath) => !modules[importPath]);
  if (missingPaths.length === 0) return;

  const loaded = await loadReactRuntimeIconModules(missingPaths);
  for (const importPath of missingPaths) {
    modules[importPath] = loaded[importPath];
  }
}

export function registerReactRuntimeBridge(): void {
  const existing = runtimeScope[reactRuntimeBridgeSymbol];
  if (existing) {
    const hasCompatibleLibraries = Object.entries(REACT_LIBRARY_VERSIONS).every(
      ([packageName, version]) => existing.LibraryVersions?.[packageName] === version
    );
    const hasRuntimeModules = Object.keys(runtimeModules).every((importPath) => existing.Modules?.[importPath]);
    if (
      existing.RuntimeApiVersion !== REACT_RUNTIME_API_VERSION ||
      existing.SdkVersion !== REACT_COMPONENT_SDK_VERSION ||
      existing.React !== React ||
      !hasCompatibleLibraries ||
      !hasRuntimeModules
    ) {
      throw new Error("A conflicting Randol React runtime bridge is already registered.");
    }

    return;
  }

  const bridge = Object.freeze({
    RuntimeApiVersion: REACT_RUNTIME_API_VERSION,
    ReactVersion: React.version,
    ReactDomVersion: ReactDom.version,
    SdkVersion: REACT_COMPONENT_SDK_VERSION,
    React,
    ReactDom: Object.freeze({ createPortal: ReactDom.createPortal }),
    JsxRuntime,
    LibraryVersions: REACT_LIBRARY_VERSIONS,
    Modules: runtimeModules,
    Sdk: Object.freeze({
      Box: MuiBox,
      Button: MuiButton,
      Stack: MuiStack,
      TextField: MuiTextField,
      Typography: MuiTypography,
      useTheme,
      useDebouncedValue,
      useDebouncedCallback,
      useChatSession,
      StyledMarkdown: SdkStyledMarkdown,
      ChatMessages: NativeChatMessages,
      ChatComposer: NativeChatComposer,
      ChatActivity,
      FilePickerDialog,
    }),
  });
  Object.defineProperty(globalThis, reactRuntimeBridgeSymbol, {
    value: bridge,
    writable: false,
    configurable: false,
    enumerable: false,
  });
}
